// SourceEventRenderer
// Dispatches per-`log_type` to a structured view of the relevant raw event JSON.
// Originally added in v1.54.0 for the Recent Events page (7 ATS-mirrored
// workloads: graph_signin, directory_audit, url_click, risk_detection,
// risky_user, security_incident, device_security). In v1.55.0 (Phase 2 of the
// three-way parity migration) AlertsPage + TestRunResultsPage migrated to
// consume this module — so it now also handles `azure_activity` and a
// structured M365 audit catch-all that covers `signin` (M365 audit format),
// exchange, sharepoint, dlp, message_trace, general, secure_score,
// security_alert. `adv_hunting` (Defender Advanced Hunting rows) has its own
// sectioned view since v1.205.0. Unknown log_types fall through to M365AuditDefaultView,
// which itself falls back to a generic scalar grid if no audit-shaped fields
// are present.
//
// Fallback values from the alert/test-row envelope (user, ip, app, operation,
// device) are accepted via the optional `fallbacks` prop and consumed by the
// `graph_signin` and M365 audit views. Recent Events does not pass fallbacks
// (no envelope) and its behavior is unchanged.

import React from 'react';
import { DetailField, CollapsibleSection, IpLink, FormatTs, stripPort } from './sharedHelpers';
import CopyButton from '../CopyButton';
import { parseAzureAuthScope, scopeTargetTypeLabel, scopeTargetName, authorizationHint } from './azureActivityHelpers';
import { parseSecurityAlert, verdictTextClass, statusTextClass, defangForDisplay } from './securityEvidenceHelpers';
import { buildAdvHuntingSections } from './advHuntingHelpers';

// Generic key/value grid for arbitrary scalar fields. Used as the fallback shape
// and as a building block inside the per-log_type renderers.
function ScalarGrid({ fields }) {
  // `fields` is an array of [label, value, opts] tuples. Skip rows with empty values.
  const visible = fields.filter((f) => f[1] !== undefined && f[1] !== null && f[1] !== '');
  if (visible.length === 0) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-1">
      {visible.map(([label, value, opts], idx) => (
        <DetailField key={`${label}-${idx}`} label={label} mono={!!(opts && opts.mono)}>
          {opts && opts.element ? value : <span>{String(value)}</span>}
        </DetailField>
      ))}
    </div>
  );
}

// Graph sign-in (rich) — mirrors AlertsPage's inline graph_signin branch in
// fields shown. Accepts optional fallbacks from the alert/test envelope when
// the raw event omits user / ip / device.
function GraphSignInView({ raw, tzIana, fallbacks = {} }) {
  const status = raw.status || {};
  const statusOk = status.errorCode === 0;
  const dd = raw.deviceDetail || {};
  const loc = raw.location || {};
  const user = raw.userPrincipalName || raw.userDisplayName || fallbacks.user;
  const ip = raw.ipAddress || fallbacks.ip;
  const deviceName = dd.displayName || dd.deviceId || fallbacks.device;
  return (
    <div className="space-y-3">
      <ScalarGrid fields={[
        ['Timestamp', <FormatTs ts={raw.createdDateTime} tzIana={tzIana} />, { element: true }],
        ['User', user, { mono: true }],
        ['IP Address', <IpLink ip={ip} />, { element: true }],
        ['Application', raw.appDisplayName],
        ['Resource', raw.resourceDisplayName ||
          (raw.resourceId ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-mono text-xs px-1.5 py-0.5 rounded border border-amber-700/60 bg-amber-900/30 text-amber-300">
                {raw.resourceId}
              </span>
              <span className="text-[10px] text-gray-500 italic">resourceId · no friendly name</span>
            </span>
          ) : null), { element: true }],
        ['Client App', raw.clientAppUsed],
        ['Device', deviceName
          ? <span className="text-gray-200">{deviceName}</span>
          : <span className="text-gray-500 italic">Unknown</span>,
          { element: true }],
        ['Auth', raw.authenticationRequirement],
        ['CA Status', raw.conditionalAccessStatus && raw.conditionalAccessStatus !== 'notApplied' ? raw.conditionalAccessStatus : null],
        ['Risk Level', raw.riskLevelDuringSignIn && raw.riskLevelDuringSignIn !== 'none'
          ? <span className="text-orange-400">{raw.riskLevelDuringSignIn}</span>
          : null,
          { element: true }],
        ['Location', [loc.city, loc.state, loc.countryOrRegion].filter(Boolean).join(', ')],
        ['User Agent', raw.userAgent],
      ]} />
      {raw.status && (
        <div className="border-t border-gray-800/40 pt-2">
          <DetailField label="Result">
            {statusOk ? (
              <span className="text-green-400">Success</span>
            ) : (
              <span className="text-red-400">
                Error {status.errorCode}
                {status.failureReason ? ` — ${status.failureReason}` : ''}
              </span>
            )}
          </DetailField>
        </div>
      )}
    </div>
  );
}

function DirectoryAuditView({ raw, tzIana }) {
  // Graph API directoryAudit schema: initiatedBy.user{userPrincipalName} OR
  // initiatedBy.app{displayName}, activityDisplayName, result, targetResources[].
  const initBy = raw.initiatedBy || {};
  const initUser = initBy.user?.userPrincipalName;
  const initApp = initBy.app?.displayName;
  const initiator = initUser
    ? <span className="font-mono">{initUser}</span>
    : initApp
      ? <span className="text-sky-300">{initApp} <span className="text-[10px] text-gray-500 italic">(app)</span></span>
      : null;
  const ip = raw.additionalDetails?.find?.((d) => d.key === 'IPAddress')?.value || raw.ipAddress;
  const targets = Array.isArray(raw.targetResources) ? raw.targetResources : [];
  return (
    <div className="space-y-3">
      <ScalarGrid fields={[
        ['Timestamp', <FormatTs ts={raw.activityDateTime || raw.createdDateTime} tzIana={tzIana} />, { element: true }],
        ['Activity', raw.activityDisplayName],
        ['Category', raw.category],
        ['Result', raw.result === 'success'
          ? <span className="text-green-400">Success</span>
          : raw.result
            ? <span className="text-red-400">{raw.result}{raw.resultReason ? ` — ${raw.resultReason}` : ''}</span>
            : null,
          { element: true }],
        ['Initiator', initiator, { element: true }],
        ['IP', <IpLink ip={ip} />, { element: true }],
      ]} />
      {targets.length > 0 && (
        <div className="border-t border-gray-800/40 pt-2 space-y-1">
          <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Target Resources</div>
          <div className="space-y-1.5">
            {targets.map((t, i) => (
              <div key={i} className="text-sm">
                <span className="text-gray-300">{t.displayName || t.userPrincipalName || t.id}</span>
                {t.type && <span className="ml-2 text-[11px] text-gray-500 italic">{t.type}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UrlClickView({ raw, tzIana }) {
  // Defender Safe Links: TimeGenerated, UserId, Url, Action, ThreatTypes, NetworkMessageId, etc.
  return (
    <div className="space-y-3">
      <ScalarGrid fields={[
        ['Timestamp', <FormatTs ts={raw.TimeGenerated || raw.createdDateTime} tzIana={tzIana} />, { element: true }],
        ['User', raw.UserId, { mono: true }],
        ['IP', <IpLink ip={raw.ClientIP || raw.ipAddress} />, { element: true }],
        ['Action', raw.Action],
        ['Threat Types', raw.ThreatTypes],
        ['Network Message ID', raw.NetworkMessageId, { mono: true }],
      ]} />
      {(raw.Url || raw.UrlClickedThrough) && (
        <div className="border-t border-gray-800/40 pt-2">
          <DetailField label="URL">
            <span className="group inline-flex items-center gap-0.5">
              <span className="break-all text-gray-300">{raw.Url || raw.UrlClickedThrough}</span>
              <CopyButton text={raw.Url || raw.UrlClickedThrough} />
            </span>
          </DetailField>
        </div>
      )}
    </div>
  );
}

function RiskDetectionView({ raw, tzIana }) {
  // Identity Protection riskDetection schema (state object, no client IP per handoff doc).
  return (
    <ScalarGrid fields={[
      ['Detected', <FormatTs ts={raw.detectedDateTime || raw.activityDateTime} tzIana={tzIana} />, { element: true }],
      ['User', raw.userPrincipalName, { mono: true }],
      ['Risk Type', raw.riskType || raw.riskEventType],
      ['Risk Level', raw.riskLevel && <span className={
        raw.riskLevel === 'high' ? 'text-red-400'
          : raw.riskLevel === 'medium' ? 'text-amber-400'
            : raw.riskLevel === 'low' ? 'text-yellow-400'
              : 'text-gray-300'
      }>{raw.riskLevel}</span>, { element: true }],
      ['State', raw.riskState],
      ['Source', raw.source],
      ['Detail', raw.riskDetail],
      ['Activity', raw.activity],
      ['IP', raw.ipAddress ? <IpLink ip={raw.ipAddress} /> : null, { element: true }],
      ['Location', raw.location ? [raw.location.city, raw.location.state, raw.location.countryOrRegion].filter(Boolean).join(', ') : null],
    ]} />
  );
}

function RiskyUserView({ raw, tzIana }) {
  return (
    <ScalarGrid fields={[
      ['Updated', <FormatTs ts={raw.riskLastUpdatedDateTime || raw.activityDateTime} tzIana={tzIana} />, { element: true }],
      ['User', raw.userPrincipalName, { mono: true }],
      ['Display Name', raw.userDisplayName],
      ['Risk State', raw.riskState],
      ['Risk Level', raw.riskLevel],
      ['Risk Detail', raw.riskDetail],
      ['Is Deleted', raw.isDeleted === true ? 'Yes' : raw.isDeleted === false ? 'No' : null],
      ['Last Activity', raw.riskLastUpdatedDateTime],
    ]} />
  );
}

function SecurityIncidentView({ raw, tzIana }) {
  return (
    <ScalarGrid fields={[
      ['Created', <FormatTs ts={raw.createdDateTime || raw.firstActivityDateTime} tzIana={tzIana} />, { element: true }],
      ['Display Name', raw.displayName],
      ['Severity', raw.severity && <span className={
        raw.severity === 'high' ? 'text-red-400'
          : raw.severity === 'medium' ? 'text-amber-400'
            : raw.severity === 'low' ? 'text-yellow-400'
              : 'text-gray-300'
      }>{raw.severity}</span>, { element: true }],
      ['Classification', raw.classification],
      ['Determination', raw.determination],
      ['Status', raw.status],
      ['Assigned To', raw.assignedTo],
      ['Tenant', raw.tenantId, { mono: true }],
    ]} />
  );
}

function DeviceSecurityView({ raw, tzIana }) {
  // Defender Advanced Hunting schema is event-shape-varied; surface the most common fields.
  return (
    <ScalarGrid fields={[
      ['Timestamp', <FormatTs ts={raw.Timestamp || raw.TimeGenerated} tzIana={tzIana} />, { element: true }],
      ['Device Name', raw.DeviceName],
      ['Device ID', raw.DeviceId, { mono: true }],
      ['Action Type', raw.ActionType],
      ['Title', raw.Title || raw.AlertTitle],
      ['Severity', raw.Severity],
      ['File / Process', raw.FileName || raw.ProcessName],
      ['Account', raw.AccountName || raw.InitiatingProcessAccountName, { mono: true }],
      ['Remote IP', raw.RemoteIP ? <IpLink ip={raw.RemoteIP} /> : null, { element: true }],
    ]} />
  );
}

// Defender Advanced Hunting row (log_type 'adv_hunting'). One wide union of columns
// across every hunting query; advHuntingHelpers groups the filled ones into sections and
// puts any column it does not know under "Other fields", so nothing is hidden.
function AdvancedHuntingView({ raw, tzIana }) {
  const sections = buildAdvHuntingSections(raw);
  if (sections.length === 0) return <GenericScalarView raw={raw} />;
  return (
    <div className="space-y-3">
      {sections.map((s) => (
        <div key={s.id}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">{s.title}</div>
          <ScalarGrid fields={s.rows.map((r) => {
            if (r.time) return [r.label, <FormatTs ts={r.value} tzIana={tzIana} />, { element: true }];
            if (r.ip) return [r.label, <IpLink ip={r.value} />, { element: true, mono: true }];
            return [r.label, r.mono ? (
              <span className="group inline-flex items-center gap-0.5 break-all">
                <span>{r.value}</span>
                <CopyButton text={r.value} />
              </span>
            ) : r.value, { element: r.mono, mono: r.mono }];
          })} />
        </div>
      ))}
    </div>
  );
}

// Azure Resource Manager activity log. For Microsoft.Authorization/* operations
// the parent scope of the artifact (role assignment / policy / lock target) is
// rendered as an inset Authorization Target sub-block, with a contextual hint
// pointing the analyst at the IAM/Policy surface they actually need to check.
function AzureActivityView({ raw, tzIana, fallbacks = {} }) {
  const opName = raw.operationName?.localizedValue || raw.operationName?.value;
  const opCode = raw.operationName?.value;
  const statusText = raw.status?.localizedValue || raw.status?.value;
  const statusColor = statusText === 'Succeeded' ? 'text-green-400'
    : statusText === 'Started' ? 'text-gray-300'
    : statusText ? 'text-red-400' : 'text-gray-500';
  const caller = raw.caller;
  const callerType = raw.claims?.idtyp;
  const appId = raw.claims?.appid;
  const rg = raw.resourceGroupName;
  const resType = raw.resourceType?.localizedValue || raw.resourceType?.value;
  const resId = raw.resourceId;
  const resName = resId ? resId.split('/').filter(Boolean).pop() : null;
  const sub = raw.subscriptionId;
  const ip = stripPort(raw.callerIpAddress || fallbacks.ip);

  return (
    <div className="space-y-3">
      {/* Row 1: When / What / Result */}
      <ScalarGrid fields={[
        ['Timestamp', raw.eventTimestamp ? <FormatTs ts={raw.eventTimestamp} tzIana={tzIana} /> : null, { element: true }],
        ['Operation', opName ? (
          <span>
            <span>{opName}</span>
            {opCode && opCode !== opName && (
              <span className="ml-1.5 text-gray-500 font-mono text-[11px]">({opCode})</span>
            )}
          </span>
        ) : null, { element: true }],
        ['Result', statusText ? <span className={statusColor}>{statusText}</span> : null, { element: true }],
      ]} />

      {/* Row 2: Resource */}
      {(rg || resType || resName) && (
        <div className="border-t border-gray-800/40 pt-2">
          <ScalarGrid fields={[
            ['Resource Group', rg ? (
              <span className="group inline-flex items-center gap-0.5">
                <span className="break-all">{rg}</span>
                <CopyButton text={rg} />
              </span>
            ) : null, { mono: true, element: true }],
            ['Resource Type', resType],
            ['Resource', resName ? (
              <span className="group inline-flex items-center gap-0.5">
                <span className="break-all">{resName}</span>
                <CopyButton text={resName} />
              </span>
            ) : null, { mono: true, element: true }],
          ]} />
        </div>
      )}

      {/* Authorization Target — surfaces the parent scope for Microsoft.Authorization/* operations */}
      {resType && resType.startsWith('Microsoft.Authorization/') && (() => {
        const parsed = parseAzureAuthScope(resId);
        if (!parsed) return null;
        const { scope, artifactType, artifactId } = parsed;
        const targetType = scopeTargetTypeLabel(scope);
        const targetName = scopeTargetName(scope);
        return (
          <div className="border-t border-amber-900/40 bg-amber-950/10 px-3 py-2 rounded space-y-1.5">
            <div className="text-[11px] font-semibold text-amber-400 uppercase tracking-wide">Authorization Target</div>
            <ScalarGrid fields={[
              ['Target Type', targetType],
              ['Target Name', (
                <span className="group inline-flex items-center gap-0.5">
                  <span className="break-all">{targetName}</span>
                  <CopyButton text={targetName} />
                </span>
              ), { mono: true, element: true }],
              ['Artifact', artifactType ? (
                <span>
                  <span>{artifactType}</span>
                  {artifactId && (
                    <span className="ml-1 text-gray-500 font-mono text-[11px]">/ {artifactId}</span>
                  )}
                </span>
              ) : null, { element: true }],
            ]} />
            <div className="text-[11px] text-amber-300/80 italic leading-snug">
              {authorizationHint(artifactType)}
            </div>
          </div>
        );
      })()}

      {/* Row 3: Caller / IP / Subscription */}
      <div className="border-t border-gray-800/40 pt-2">
        <ScalarGrid fields={[
          ['Caller', caller ? (
            <span className="group inline-flex items-center gap-0.5">
              <span className="break-all">{caller}</span>
              {callerType && (
                <span className="ml-1 text-[10px] text-gray-500 uppercase">({callerType})</span>
              )}
              <CopyButton text={caller} />
            </span>
          ) : null, { mono: true, element: true }],
          ['IP Address', <IpLink ip={ip} />, { mono: true, element: true }],
          ['Subscription', sub ? (
            <span className="group inline-flex items-center gap-0.5" title={sub}>
              <span>{sub.length > 12 ? `${sub.substring(0, 8)}…` : sub}</span>
              <CopyButton text={sub} />
            </span>
          ) : null, { mono: true, element: true }],
        ]} />
      </div>

      {/* Full Resource ID + (optional) App ID */}
      {(resId || (appId && callerType === 'app')) && (
        <div className="border-t border-gray-800/40 pt-2 space-y-1">
          {resId && (
            <DetailField label="Resource ID" mono>
              <span className="group inline-flex items-start gap-0.5">
                <span className="break-all text-gray-500">{resId}</span>
                <CopyButton text={resId} />
              </span>
            </DetailField>
          )}
          {appId && callerType === 'app' && (
            <DetailField label="App ID" mono>
              <span className="group inline-flex items-center gap-0.5">
                <span>{appId}</span>
                <CopyButton text={appId} />
              </span>
            </DetailField>
          )}
        </div>
      )}
    </div>
  );
}

// Last-resort fallback for unfamiliar schemas: render every top-level scalar
// as a generic key/value grid (capped to avoid runaway output).
function GenericScalarView({ raw }) {
  if (!raw || typeof raw !== 'object') return null;
  const entries = Object.entries(raw)
    .filter(([_, v]) => v != null && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
    .slice(0, 24);
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-gray-500 italic">No structured view for this log_type yet — showing top-level fields:</div>
      <ScalarGrid fields={entries.map(([k, v]) => [k, String(v)])} />
    </div>
  );
}

// M365 audit catch-all: signin (M365 audit format, distinct from graph_signin),
// exchange, sharepoint, dlp, message_trace, general, secure_score,
// security_alert. Mirrors AlertsPage's inline default branch. Falls through to
// GenericScalarView when no recognizable M365 audit fields are present.
function M365AuditDefaultView({ raw, tzIana, fallbacks = {} }) {
  if (!raw || typeof raw !== 'object') return null;
  const eventTime = raw.CreationTime || raw.EventTimeUTC;
  const actor = raw.UserId || raw.UserPrincipalName || fallbacks.user;
  const ip = raw.ClientIP || fallbacks.ip;
  const operation = raw.Operation || fallbacks.operation;
  const application = fallbacks.app || raw.ApplicationId;

  // Detect M365-audit shape — if none of these markers exist, defer to the
  // generic scalar fallback so unknown schemas still get something usable.
  const looksLikeM365Audit = !!(eventTime || raw.UserId || raw.UserPrincipalName ||
    raw.Operation || raw.ClientIP || raw.ResultStatus || raw.ApplicationId);
  if (!looksLikeM365Audit) {
    return <GenericScalarView raw={raw} />;
  }

  const owner = raw.MailboxOwnerUPN ||
    (Array.isArray(raw.set_MailboxOwnerUPN) && raw.set_MailboxOwnerUPN.length > 0
      ? raw.set_MailboxOwnerUPN.join(', ')
      : null);
  const showOwner = owner && owner !== actor;

  return (
    <ScalarGrid fields={[
      ['Timestamp', eventTime ? <FormatTs ts={eventTime} tzIana={tzIana} /> : null, { element: true }],
      ['User', actor ? (
        <span className="group inline-flex items-center gap-0.5">
          <span>{actor}</span>
          <CopyButton text={actor} />
        </span>
      ) : null, { mono: true, element: true }],
      ['Mailbox Owner', showOwner ? <span className="text-amber-300">{owner}</span> : null, { mono: true, element: true }],
      ['Operation', operation],
      ['IP Address', <IpLink ip={ip} />, { mono: true, element: true }],
      ['Result', raw.ResultStatus != null ? (
        <span className={raw.ResultStatus === 'True' || raw.ResultStatus === 'Success' ? 'text-green-400' : 'text-gray-300'}>
          {raw.ResultStatus}
        </span>
      ) : null, { element: true }],
      ['Application', application ? (
        <span className="text-sky-300 font-medium">{application}</span>
      ) : null, { element: true }],
      ['Client Info', raw.ClientInfoString],
    ]} />
  );
}

// ── Defender security_alert (Graph Security API) ──────────────────────────────
// The high-signal content is the alert's evidence[] array (file / process / user
// / ip / url / device). Per-family emphasis (FAMILY_PROFILES): primary evidence
// is rendered first + expanded, context is collapsed. See securityEvidenceHelpers.

function ClampText({ text }) {
  const [open, setOpen] = React.useState(false);
  const s = String(text);
  if (s.length <= 120) return <span className="font-mono break-all">{s}</span>;
  return (
    <span className="font-mono break-all">
      {open ? s : `${s.slice(0, 120)}…`}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="ml-1 text-sky-400 hover:text-sky-300 text-[11px]"
      >
        {open ? 'less' : 'more'}
      </button>
    </span>
  );
}

// Renders an evidence field value per the display flags carried in `opts`
// (ip / ts / url / clamp / copy / mono / warn / emphasis).
function EvidenceValue({ value, opts, tzIana }) {
  if (opts.ip) return <IpLink ip={value} />;
  if (opts.ts) return <FormatTs ts={value} tzIana={tzIana} />;
  if (opts.url) {
    return (
      <span className="group inline-flex items-center gap-0.5 max-w-full">
        <span className="font-mono text-rose-300 break-all" title={value}>{defangForDisplay(value)}</span>
        <CopyButton text={String(value)} />
      </span>
    );
  }
  if (opts.clamp) return <ClampText text={value} />;
  const cls = [
    opts.mono ? 'font-mono' : '',
    opts.warn ? 'text-amber-400 font-medium' : '',
    opts.emphasis ? 'text-gray-100 font-medium' : '',
  ].filter(Boolean).join(' ');
  if (opts.copy) {
    return (
      <span className="group inline-flex items-center gap-0.5 max-w-full">
        <span className={`break-all ${cls}`}>{String(value)}</span>
        <CopyButton text={String(value)} />
      </span>
    );
  }
  return <span className={`break-all ${cls}`}>{String(value)}</span>;
}

function EvidenceGrid({ fields, tzIana }) {
  const visible = fields.filter((f) => f[1] !== undefined && f[1] !== null && f[1] !== '');
  if (visible.length === 0) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-1">
      {visible.map(([label, value, opts], idx) => (
        <DetailField key={`${label}-${idx}`} label={label}>
          <EvidenceValue value={value} opts={opts || {}} tzIana={tzIana} />
        </DetailField>
      ))}
    </div>
  );
}

function EvidenceCard({ ev, tzIana }) {
  const { spec, verdict, detectionStatus, remediationStatus } = ev;
  return (
    <div className="border-t border-gray-800/40 pt-2 space-y-1.5">
      <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
        <span className="text-[11px] font-semibold text-gray-300 uppercase tracking-wide">{spec.label}</span>
        {verdict && verdict !== 'unknown' && (
          <span className={`text-[11px] font-medium ${verdictTextClass(verdict)}`}>{verdict}</span>
        )}
        {detectionStatus && (
          <span className={`text-[11px] ${statusTextClass(detectionStatus)}`}>detection: {detectionStatus}</span>
        )}
        {remediationStatus && (
          <span className={`text-[11px] ${statusTextClass(remediationStatus)}`}>remediation: {remediationStatus}</span>
        )}
      </div>
      <EvidenceGrid fields={spec.fields(ev.raw)} tzIana={tzIana} />
    </div>
  );
}

function SecurityAlertView({ raw, tzIana }) {
  const parsed = parseSecurityAlert(raw);
  if (!parsed) return <GenericScalarView raw={raw} />;
  const { header, primary, context } = parsed;
  const sevClass = header.severity === 'high' ? 'text-red-400'
    : header.severity === 'medium' ? 'text-amber-400'
    : header.severity === 'low' ? 'text-yellow-400'
    : 'text-gray-300';

  return (
    <div className="space-y-3">
      {/* Alert header */}
      <ScalarGrid fields={[
        ['Severity', header.severity ? <span className={sevClass}>{header.severity}</span> : null, { element: true }],
        ['Category', header.category],
        ['Status', header.status],
        ['Detection Source', header.detectionSource],
        ['Service Source', header.serviceSource],
        ['Determination', header.determination && header.determination !== 'unknown' ? header.determination : null],
        ['Threat Family', header.threatFamily],
        ['First Activity', header.firstActivityDateTime ? <FormatTs ts={header.firstActivityDateTime} tzIana={tzIana} /> : null, { element: true }],
        ['Last Activity', header.lastActivityDateTime ? <FormatTs ts={header.lastActivityDateTime} tzIana={tzIana} /> : null, { element: true }],
      ]} />

      {header.description && (
        <DetailField label="Description">
          <span className="text-gray-300 leading-snug">{header.description}</span>
        </DetailField>
      )}

      {header.mitreTechniques.length > 0 && (
        <DetailField label="MITRE Techniques">
          <span className="inline-flex flex-wrap gap-1.5">
            {header.mitreTechniques.map((t) => (
              <a
                key={t}
                href={`https://attack.mitre.org/techniques/${String(t).replace('.', '/')}/`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="px-1.5 py-0.5 rounded bg-gray-800 text-sky-300 hover:text-sky-200 font-mono text-[11px]"
              >
                {t}
              </a>
            ))}
          </span>
        </DetailField>
      )}

      {/* Primary evidence — rendered first, expanded */}
      {primary.map((ev, i) => <EvidenceCard key={`p-${i}`} ev={ev} tzIana={tzIana} />)}

      {/* Context evidence — present but collapsed */}
      {context.length > 0 && (
        <div className="border-t border-gray-800/40 pt-2">
          <CollapsibleSection title={`Additional evidence (${context.length})`}>
            <div className="space-y-2">
              {context.map((ev, i) => <EvidenceCard key={`c-${i}`} ev={ev} tzIana={tzIana} />)}
            </div>
          </CollapsibleSection>
        </div>
      )}

      {header.recommendedActions && (
        <div className="border-t border-gray-800/40 pt-2">
          <CollapsibleSection title="Recommended Actions">
            <p className="text-gray-300 whitespace-pre-wrap leading-snug text-xs">{header.recommendedActions}</p>
          </CollapsibleSection>
        </div>
      )}

      {header.alertWebUrl && (
        <div className="border-t border-gray-800/40 pt-2 group">
          <a
            href={header.alertWebUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-400 hover:text-blue-300 hover:underline text-xs"
          >
            Open in Microsoft Defender ↗
          </a>
          <CopyButton text={header.alertWebUrl} />
        </div>
      )}
    </div>
  );
}

const RENDERERS = {
  graph_signin:      GraphSignInView,
  directory_audit:   DirectoryAuditView,
  url_click:         UrlClickView,
  risk_detection:    RiskDetectionView,
  risky_user:        RiskyUserView,
  security_incident: SecurityIncidentView,
  security_alert:    SecurityAlertView,
  device_security:   DeviceSecurityView,
  adv_hunting:       AdvancedHuntingView,
  azure_activity:    AzureActivityView,
};

export default function SourceEventRenderer({ rawEvent, logType, tzIana, fallbacks }) {
  if (!rawEvent) {
    return <div className="text-[11px] text-gray-500 italic">No source event available.</div>;
  }
  const View = RENDERERS[logType];
  if (View) {
    return <View raw={rawEvent} tzIana={tzIana} fallbacks={fallbacks} />;
  }
  // Unknown log_type — defer to the M365 audit view, which itself falls
  // through to GenericScalarView when no audit-shaped fields are present.
  return <M365AuditDefaultView raw={rawEvent} tzIana={tzIana} fallbacks={fallbacks} />;
}
